import { useMemo, useCallback } from 'react'
import {
  useQueryStates,
  parseAsInteger,
  parseAsArrayOf,
  parseAsString,
} from 'nuqs'
import { addDays, startOfDay } from 'date-fns'
import { kebabCaseUrlKeys } from 'lib/nuqs/kebabCaseUrlKeys'
import { parseAsZonedDate } from 'lib/nuqs/parseAsZonedDate'
import type { FiltersFormValues } from '../filtersSchema'

export const DEFAULT_EVENT_LOGS_LIMIT = 50
export const DEFAULT_EVENT_LOGS_DATE_WINDOW_DAYS = 7

// URL ↔ form bridge for EventLogs filters: nuqs-backed read in RHF/widget shape, with a setter that decomposes form values back to URL params.
export const useEventLogsFiltersQueryState = () => {
  const { startDefault, endDefault } = useMemo(() => {
    const end = startOfDay(new Date())
    return {
      startDefault: startOfDay(
        addDays(end, -DEFAULT_EVENT_LOGS_DATE_WINDOW_DAYS)
      ),
      endDefault: end,
    }
  }, [])

  const parsers = {
    startDate: parseAsZonedDate.withDefault(startDefault),
    endDate: parseAsZonedDate.withDefault(endDefault),
    companyId: parseAsInteger,
    userId: parseAsInteger,
    subjectUserId: parseAsInteger,
    shiftId: parseAsInteger,
    eventType: parseAsArrayOf(parseAsString),
    messageContains: parseAsString,
    limit: parseAsInteger.withDefault(DEFAULT_EVENT_LOGS_LIMIT),
  }

  const [raw, setRaw] = useQueryStates(parsers, {
    urlKeys: kebabCaseUrlKeys(parsers),
  })

  const values: FiltersFormValues = useMemo(
    () => ({
      dateRange: { from: raw.startDate, to: raw.endDate },
      companyId: raw.companyId,
      userId: raw.userId,
      subjectUserId: raw.subjectUserId,
      shiftId: raw.shiftId,
      eventType: (raw.eventType ?? []).join(', '),
      messageContains: raw.messageContains ?? '',
      limit: raw.limit,
    }),
    [raw]
  )

  const setValues = useCallback(
    (next: FiltersFormValues) => {
      const eventType = next.eventType
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
      return setRaw({
        startDate: next.dateRange.from,
        endDate: next.dateRange.to,
        companyId: next.companyId ?? null,
        userId: next.userId ?? null,
        subjectUserId: next.subjectUserId ?? null,
        shiftId: next.shiftId ?? null,
        eventType: eventType.length > 0 ? eventType : null,
        messageContains: next.messageContains.trim() || null,
        limit: next.limit,
      })
    },
    [setRaw]
  )

  return [values, setValues] as const
}
